import * as styles from "./AmendmentGenerateDialog.css";

import { type JSX, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  DatePicker,
  DatePickerContent,
  DatePickerTrigger,
  DatePickerValue,
} from "@frontend/components/atoms/DatePicker";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { Plural, Trans, useLingui } from "@lingui/react/macro";

const DIALOG_WIDTH = 440;

function todayIso(): string {
  const now = new Date();

  return [
    String(now.getFullYear()),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
}

export type AmendmentGenerateDialogProps = {
  count: number;
  onCancel: () => void;
  onConfirm: (generatedOn: string) => void;
};

export function AmendmentGenerateDialog({
  count,
  onCancel,
  onConfirm,
}: AmendmentGenerateDialogProps): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const [date, setDate] = useState<string | null>(todayIso);

  const future = null !== date && date > todayIso();

  const close = () => {
    setDate(todayIso());
    onCancel();
  };

  const confirm = () => {
    if (null === date) {
      return;
    }

    setDate(todayIso());
    onConfirm(date);
  };

  return (
    <Dialog
      open={count > 0}
      onOpenChange={(next) => {
        if (!next) {
          close();
        }
      }}
    >
      <DialogContent size={DIALOG_WIDTH}>
        <DialogHeader>
          <DialogTitle>
            <Plural
              value={count}
              one="Generate the amendment"
              other="Generate # amendments"
            />
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <DialogDescription>
          <Plural
            value={count}
            one="The amendment is dated on this day."
            other="The amendments are dated on this day."
          />
        </DialogDescription>

        <div className={styles.body}>
          <Field>
            <FieldLabel>{t`Generation date`}</FieldLabel>

            <FieldContent>
              <DatePicker locale={locale} value={date} onValueChange={setDate}>
                <DatePickerTrigger>
                  <DatePickerValue placeholder={t`Choose a date`} />
                </DatePickerTrigger>

                <DatePickerContent />
              </DatePicker>
            </FieldContent>
          </Field>

          {future ? (
            <p className={styles.warning} role="alert">
              <Trans>
                This date is in the future. Confirm only if the amendment must
                really be dated on it.
              </Trans>
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="neutral" onClick={close}>
            <Trans>Cancel</Trans>
          </Button>

          <Button variant="primary" disabled={null === date} onClick={confirm}>
            {future ? <Trans>Generate anyway</Trans> : <Trans>Generate</Trans>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
